import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import { User } from '@Types/user';
import { buildFilterParams } from '@Utils/buildFilterParams';
import { formatPhoneNumber } from '@Utils/utils';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import { useUsers } from '@Hooks/useUsers';
import { Contact } from 'lucide-react';
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import UserQuickFilter from './UserQuickFilters';

const userColumnConfigs: ColumnConfig<User>[] = [
  {
    id: 'name',
    headerLabel: 'Name',
    accessor: (row) => row.name || '-',
    type: 'text',
    size: 120,
  },
  {
    id: 'email',
    headerLabel: 'Email',
    accessor: (row) => row.email || '-',
    type: 'text',
    size: 120,
  },
  {
    id: 'phoneNumber',
    headerLabel: 'Phone Number',
    accessor: (row) => row.phoneNumber || '-',
    type: 'text',
    cell: (info) => {
      const value = info.getValue();
      return value ? formatPhoneNumber(value) : 'N/A';
    },
    enableSorting: false,
  },
  {
    id: 'userRoles',
    headerLabel: 'User Roles',
    accessor: (row) => row.role || '-',
    type: 'text',
    cell: (cell) => {
      const roles =
        cell?.row?.original?.roleAssignments?.map((ra) => ra?.userRole?.name) ??
        [];

      return (
        <div className="flex flex-col">
          {roles.map((role, index) => (
            <Badge key={index} variant="secondary">
              {role || '-'}
            </Badge>
          ))}
        </div>
      );
    },
    enableSorting: false,
  },
  {
    id: 'createdAt',
    headerLabel: 'Created At',
    accessor: (row) => row.createdAt ?? '',
    type: 'text',
    cell: (info) => {
      const date = info.getValue();
      if (!date) return 'N/A';
      const dt = DateTime.fromISO(date);
      const dateString = dt.toLocaleString(DateTime.DATETIME_MED);
      return dateString || 'N/A';
    },
  },
];

const USER_FIELD_MAPPING: Record<string, string> = {
  name: 'filter[name]',
  email: 'filter[email]',
  phoneNumber: 'filter[phoneNumber]',
  userRole: 'filter[roleAssignments][some][userRole][name]',
};

const USER_STATIC_PARAMS: Record<string, string> = {
  'filter[deletedAt]': 'null',
};

export default function UserList() {
  const navigate = useNavigate();

  const controller = useResourceTableController<User>({
    columnsConfig: userColumnConfigs,
    defaultSort: {
      id: 'createdAt',
      desc: false,
    },
    baseSearchParams: () => ({}),
    buildFilterParams: (filters) =>
      buildFilterParams(filters, {
        fieldMapping: USER_FIELD_MAPPING,
        staticParams: USER_STATIC_PARAMS,
      }),
    quickFilterFields: [
      { field: 'name', label: 'Name' },
      { field: 'email', label: 'Email' },
      { field: 'phoneNumber', label: 'Phone Number' },
    ],
    initialPageSize: 10,
    initialPinning: {
      left: ['select', 'name'],
      right: [],
    },
  });

  const { data: payload, status } = useUsers({
    searchParams: controller.searchParams,
    enabled: true,
  });

  const rows: User[] = Array.isArray(payload) ? payload : (payload?.data ?? []);

  const meta = Array.isArray(payload) ? undefined : payload?.meta;

  return (
    <ResourceTableView<User>
      label="Users"
      summaryIcon={<Contact />}
      controller={controller}
      data={rows}
      meta={meta}
      status={status as any}
      onRowClick={(row) => navigate(`/users/${row.id}`)}
      renderQuickFiltersExtra={({ table }) => (
        <UserQuickFilter<User> table={table} />
      )}
    />
  );
}
